import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatBRL, formatBRLFull, orgaoFormal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, ArrowLeft, FileText, Pencil } from "lucide-react";
import { toast } from "sonner";

const MESES_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function formatarMesAno(mesAno) {
  // "2026-08" → "Agosto de 2026"
  if (!mesAno || typeof mesAno !== "string") return "";
  const [ano, mes] = mesAno.split("-");
  const idx = parseInt(mes, 10) - 1;
  if (isNaN(idx) || idx < 0 || idx > 11) return mesAno;
  return `${MESES_PT[idx]} de ${ano}`;
}

export default function Proposta() {
  const { cardId } = useParams();
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [curso, setCurso] = useState(null);
  const [mun, setMun] = useState(null);
  const [pessoas, setPessoas] = useState([]);
  const [emissores, setEmissores] = useState([]);
  const [emissor, setEmissor] = useState(null);
  const [nucleo, setNucleo] = useState(null);
  const [qtd, setQtd] = useState(1);
  const [valorNegociadoUnit, setValorNegociadoUnit] = useState(0); // Iter 40: só a vendedora entra o valor negociado
  const [selPessoas, setSelPessoas] = useState([]);
  // Iter 35: gate form → documento
  const [gerado, setGerado] = useState(false);

  useEffect(() => {
    (async () => {
      const cd = await api.get(`/cards`, { params: {} }).catch(() => null);
      // Fetch card directly
      const cs = await api.get(`/cursos`);
      // Actually we need a card endpoint. Let's use inscricoes/cards workaround.
      // Simpler: fetch all cards for user's cursos and find the one
      const cards = [];
      for (const c of cs.data) {
        const kr = await api.get(`/cards?curso_id=${c.id}`);
        cards.push(...kr.data);
      }
      const k = cards.find((x) => x.id === cardId);
      if (!k) { toast.error("Card não encontrado"); nav(-1); return; }
      setCard(k);
      const [cr, mr, pr, er] = await Promise.all([
        api.get(`/cursos/${k.curso_id}`),
        api.get(`/municipios/${k.municipio_id}`),
        api.get(`/pessoas?municipio_id=${k.municipio_id}`),
        api.get(`/emissores`),
      ]);
      setCurso(cr.data); setMun(mr.data); setPessoas(pr.data);
      const ativos = er.data.filter((e) => e.ativo);
      setEmissores(ativos);
      if (ativos.length > 0) setEmissor(ativos[0]);
      // Iter 40: valor negociado inicia igual ao valor cheio (desconto zero)
      if (cr.data?.valor_cheio) setValorNegociadoUnit(cr.data.valor_cheio);
      // Iter 40: busca núcleo (diretor + assinatura para o rodapé da proposta)
      if (cr.data?.nucleo_id) {
        api.get(`/nucleos`).then((rn) => {
          const n = (rn.data || []).find((x) => x.id === cr.data.nucleo_id);
          if (n) setNucleo(n);
        }).catch(() => {});
      }
    })();
  }, [cardId, nav]);

  if (!card || !curso || !mun) return <div className="p-8 text-slate-500">Carregando proposta...</div>;

  const valorTabelaUnit = Number(curso?.valor_cheio || 0);
  const valorUnit = Number(valorNegociadoUnit || 0);
  const totalCheio = valorTabelaUnit * Number(qtd || 0);
  const totalVendido = valorUnit * Number(qtd || 0);
  const desconto = Math.max(0, totalCheio - totalVendido);
  const descontoUnitario = Math.max(0, valorTabelaUnit - valorUnit);
  const dataHoje = new Date().toLocaleDateString("pt-BR");
  const validade = new Date(); validade.setDate(validade.getDate() + 15);
  const dataValidade = validade.toLocaleDateString("pt-BR");

  const togglePessoa = (pid) => {
    setSelPessoas((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
  };

  const podeGerar = Number(qtd) > 0 && Number(valorNegociadoUnit) > 0 && emissor;
  const cidadeCurso = curso?.local_realizacao || curso?.uf || "";
  const mesAnoFmt = formatarMesAno(curso?.mes_ano);
  // Frase final da data: "20 a 22 de Agosto de 2026, em Ribeirão Preto/SP"
  const fraseData = [
    curso?.data_realizacao,
    mesAnoFmt ? `de ${mesAnoFmt}` : "",
    cidadeCurso ? `, em ${cidadeCurso}` : "",
  ].filter(Boolean).join(" ");

  // ============================================================
  // TELA 1 — Formulário (antes de gerar o documento)
  // ============================================================
  if (!gerado) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4" data-testid="proposta-form-page">
        <div className="mb-4">
          <Button variant="outline" onClick={() => nav(-1)} data-testid="btn-voltar-form">
            <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
          </Button>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-lg bg-blue-900 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Emitir Proposta Comercial</div>
              <h1 className="font-display font-bold text-xl text-slate-900">{curso.nome}</h1>
            </div>
          </div>
          <div className="text-sm text-slate-600 mb-6">
            Destino: <b>{orgaoFormal(mun)} / {mun.uf}</b>
          </div>

          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold">CNPJ Emissor</Label>
              <p className="text-xs text-slate-500 mb-1">Quem fatura este curso.</p>
              <Select value={emissor?.id || ""} onValueChange={(v) => setEmissor(emissores.find((e) => e.id === v))}>
                <SelectTrigger data-testid="sel-emissor"><SelectValue placeholder="Selecionar CNPJ" /></SelectTrigger>
                <SelectContent>
                  {emissores.map((e) => (<SelectItem key={e.id} value={e.id}>{e.razao_social}</SelectItem>))}
                </SelectContent>
              </Select>
              {emissores.length === 0 && (
                <div className="text-xs text-red-600 mt-1">Nenhum emissor de CNPJ cadastrado. Peça ao Diretor para cadastrar em <b>Admin → Emissores</b>.</div>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-semibold">Quantidade de inscritos</Label>
                <Input type="number" min={1} value={qtd} onChange={(e) => setQtd(e.target.value)} data-testid="prop-qtd" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Valor de tabela (unitário)</Label>
                <Input type="number" step="0.01" value={valorTabelaUnit} readOnly disabled className="bg-slate-100 cursor-not-allowed" data-testid="prop-valor-tabela" />
                <p className="text-xs text-slate-500 mt-1">Automático — vem do cadastro do curso.</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Valor negociado (por aluno)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={valorNegociadoUnit}
                  onChange={(e) => setValorNegociadoUnit(e.target.value)}
                  data-testid="prop-valor-negociado"
                />
                {descontoUnitario > 0 && (
                  <p className="text-xs text-emerald-600 mt-1">
                    Desconto: {formatBRLFull(descontoUnitario)}/aluno · {formatBRLFull(desconto)} total
                  </p>
                )}
              </div>
            </div>

            {/* Preview do total */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Total da proposta</div>
                <div className="text-2xl font-display font-bold text-blue-900 tabular-nums">{formatBRLFull(totalVendido)}</div>
              </div>
              <div className="text-xs text-slate-500 text-right">
                {Number(qtd) || 0} × {formatBRLFull(valorUnit)}
                {desconto > 0 && (<><br />desconto de {formatBRLFull(desconto)}</>)}
              </div>
            </div>

            {pessoas.length > 0 && (
              <div>
                <Label className="text-sm font-semibold">Nominar inscritos (opcional)</Label>
                <p className="text-xs text-slate-500 mb-1">Marque os servidores que aparecerão nominalmente na proposta.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-48 overflow-y-auto border rounded-lg p-2 bg-white">
                  {pessoas.map((p) => (
                    <label key={p.id} className={`text-xs flex items-center gap-1 p-1 rounded cursor-pointer ${selPessoas.includes(p.id) ? "bg-blue-50" : ""}`}>
                      <input type="checkbox" checked={selPessoas.includes(p.id)} onChange={() => togglePessoa(p.id)} />
                      <span className="truncate">{p.nome}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-slate-200 flex justify-end">
              <Button
                onClick={() => setGerado(true)}
                disabled={!podeGerar}
                className="bg-blue-900 hover:bg-blue-800"
                data-testid="btn-gerar-proposta"
              >
                <FileText className="w-4 h-4 mr-2" /> Gerar Proposta
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // TELA 2 — Documento gerado
  // ============================================================
  return (
    <div className="max-w-4xl mx-auto py-6" data-testid="proposta-page">
      {/* Barra de ações (não imprimem) */}
      <div className="mb-6 no-print flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-200">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
          <Button variant="outline" onClick={() => setGerado(false)} data-testid="btn-editar-valores">
            <Pencil className="w-4 h-4 mr-1" /> Editar valores
          </Button>
        </div>
        <Button onClick={() => window.print()} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-imprimir">
          <Printer className="w-4 h-4 mr-2" /> Imprimir / Salvar PDF
        </Button>
      </div>

      {/* DOCUMENTO IMPRIMÍVEL */}
      <div className="proposta-doc bg-white p-10 rounded-2xl shadow-sm print:shadow-none print:rounded-none print:p-0">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between pb-6 border-b-4 border-blue-800">
          <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_dashboard-ceap/artifacts/b4qjckp1_nova_logo_CEAP_2020-removebg-preview.png" alt="CEAP Brasil" className="h-20" />
          {emissor && (
            <div className="text-right text-xs text-slate-700">
              <div className="font-bold text-slate-900 text-sm">{emissor.razao_social}</div>
              <div>CNPJ: {emissor.cnpj}</div>
              {emissor.endereco && <div>{emissor.endereco}</div>}
              {emissor.email && <div>{emissor.email}</div>}
              {emissor.telefone && <div>{emissor.telefone}</div>}
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <div className="text-xs uppercase tracking-widest text-slate-500">Proposta Comercial</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Nº {new Date().getTime().toString().slice(-8)}/{new Date().getFullYear()}</h1>
          <div className="text-sm text-slate-600 mt-1">Data: {dataHoje} · Validade: {dataValidade}</div>
        </div>

        {/* Destinatário */}
        <div className="mt-8 border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Destinatário</div>
          <div className="font-bold text-slate-900">{orgaoFormal(mun)} / {mun.uf}</div>
          {mun.cnpj && <div className="text-sm text-slate-700">CNPJ: {mun.cnpj}</div>}
          {mun.endereco && <div className="text-sm text-slate-700">{mun.endereco}</div>}
          {mun.email_institucional && <div className="text-sm text-slate-700">{mun.email_institucional}</div>}
        </div>

        {/* Objeto */}
        <div className="mt-8">
          <h2 className="font-display font-bold text-xl text-blue-900 border-b border-slate-200 pb-2 mb-3">1. Objeto da Proposta</h2>
          <p className="text-sm text-slate-800 leading-relaxed text-justify">
            O <b>Centro de Estudos da Administração Pública — CEAP Brasil</b>, instituição com mais de
            15 anos de atuação especializada em capacitação e aperfeiçoamento de agentes públicos, vem,
            respeitosamente, apresentar proposta para participação de representantes deste Órgão no
            curso <b>"{curso.nome}"</b>, a ser realizado nos dias <b>{fraseData || curso.data_realizacao}</b>,
            com carga horária compatível com a formação e o desenvolvimento profissional dos participantes.
          </p>
        </div>

        {/* Inscritos */}
        {selPessoas.length > 0 && (
          <div className="mt-6">
            <h2 className="font-display font-bold text-xl text-blue-900 border-b border-slate-200 pb-2 mb-3">2. Servidores Indicados</h2>
            <ol className="list-decimal ml-6 text-sm text-slate-800 space-y-0.5">
              {selPessoas.map((pid) => {
                const p = pessoas.find((x) => x.id === pid);
                return <li key={pid}>{p?.nome} — {p?.funcao}{p?.partido ? ` (${p.partido})` : ""}</li>;
              })}
            </ol>
          </div>
        )}

        {/* Valores */}
        <div className="mt-6">
          <h2 className="font-display font-bold text-xl text-blue-900 border-b border-slate-200 pb-2 mb-3">{selPessoas.length > 0 ? "3" : "2"}. Investimento</h2>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-blue-900 text-white">
                <th className="p-2 text-left">Descrição</th>
                <th className="p-2 text-right">Qtd.</th>
                <th className="p-2 text-right">Valor unitário</th>
                <th className="p-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-200">
                <td className="p-2">Inscrição no curso "{curso.nome}"</td>
                <td className="p-2 text-right tabular-nums">{qtd}</td>
                <td className="p-2 text-right tabular-nums">{formatBRLFull(valorTabelaUnit)}</td>
                <td className="p-2 text-right tabular-nums">{formatBRLFull(totalCheio)}</td>
              </tr>
              {desconto > 0 && (
                <tr className="border-b border-slate-200 text-emerald-700 font-semibold">
                  <td className="p-2" colSpan={3}>Desconto concedido</td>
                  <td className="p-2 text-right tabular-nums">- {formatBRLFull(desconto)}</td>
                </tr>
              )}
              <tr className="bg-slate-100 font-bold">
                <td className="p-2" colSpan={3}>VALOR TOTAL DA PROPOSTA</td>
                <td className="p-2 text-right tabular-nums text-blue-900 text-lg">{formatBRLFull(totalVendido)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Condições */}
        <div className="mt-6">
          <h2 className="font-display font-bold text-xl text-blue-900 border-b border-slate-200 pb-2 mb-3">{selPessoas.length > 0 ? "4" : "3"}. Condições Gerais</h2>
          <ul className="text-sm text-slate-800 leading-relaxed list-disc ml-6 space-y-1">
            <li>O <b>Kit do aluno com apostila física</b>, certificado digital e <b>Coffee Break</b> estão inclusos no valor.</li>
            <li>Forma de pagamento: mediante empenho prévio da despesa e/ou nota de empenho emitida em nome do CEAP Brasil.</li>
            <li>Prazo de pagamento: até 30 (trinta) dias após a realização do evento, mediante apresentação da respectiva nota fiscal.</li>
            <li>Esta proposta é firme e irrevogável dentro do prazo de validade indicado no cabeçalho.</li>
            <li>A execução dos serviços fica condicionada à confirmação por parte do Órgão contratante.</li>
          </ul>
        </div>

        {/* Fundamentação legal */}
        <div className="mt-6">
          <h2 className="font-display font-bold text-xl text-blue-900 border-b border-slate-200 pb-2 mb-3">{selPessoas.length > 0 ? "5" : "4"}. Fundamentação Legal</h2>
          <p className="text-sm text-slate-800 leading-relaxed text-justify">
            A presente proposta poderá ser contratada por meio de <b>dispensa</b> ou <b>inexigibilidade de licitação</b>,
            nos termos do art. 74, inciso III, alíneas "d" e "f", ou art. 75, inciso IV, alínea "f", da Lei Federal
            nº 14.133/2021 (Nova Lei de Licitações), a critério do Órgão contratante.
          </p>
        </div>

        {/* Assinatura */}
        <div className="mt-12 text-center" data-testid="proposta-assinatura">
          <div className="text-sm text-slate-700">Atenciosamente,</div>
          {nucleo?.assinatura_url && (
            <img
              src={nucleo.assinatura_url.startsWith("http") ? nucleo.assinatura_url : `${process.env.REACT_APP_BACKEND_URL}${nucleo.assinatura_url}`}
              alt="Assinatura"
              className="h-16 mx-auto mt-4 print:mt-2"
            />
          )}
          <div className="mt-4 border-t border-slate-400 w-72 mx-auto pt-2 text-sm">
            <b>{nucleo?.diretor_nome || emissor?.razao_social || "CEAP Brasil"}</b><br />
            <span className="text-xs">
              {nucleo?.diretor_nome
                ? `Diretor(a) do Núcleo ${nucleo?.nome || ""}`
                : `CNPJ ${emissor?.cnpj || "—"}`}
            </span>
          </div>
        </div>

        {/* Rodapé */}
        <div className="mt-12 pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
          Centro de Estudos da Administração Pública — CEAP Brasil<br />
          Capacitação profissional para servidores públicos municipais
        </div>
      </div>
    </div>
  );
}
